import type {SxProps, Theme} from "@mui/material/styles";
import type {SelectProps} from "@mui/material/Select";
import {useMutation, useQueryClient} from "react-query";
import {
    FloorplanReservationItemDTO
} from "crqlar-react-common/dist/models/floorplan/floorplanReservationItem.dto";
import {
    Reservation,
    ReservationStatus
} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {ReservationService} from "crqlar-react-common/dist/services/reservation/reservation.service";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import ReservationStatusSelect from "../../../ReservationStatusSelect";
import {useReadStore} from "../hooks/useReadStore";

interface Props {
    reservationItem: FloorplanReservationItemDTO;
    ariaLabel?: string;
    open?: boolean;
    onOpen?: SelectProps['onOpen'];
    onClose?: SelectProps['onClose'];
    sx?: SxProps<Theme>;
}

const FloorplanReservationStatusSelect = ({
    reservationItem,
    ariaLabel,
    open,
    onOpen,
    onClose,
    sx,
}: Props) => {
    const queryClient = useQueryClient();
    const patchReadStore = useReadStore(state => state.patch);
    const readOnly = useReadOnly();
    const reservation = reservationItem.restaurantReservation;

    const reservationMutation = useMutation(
        async (status: ReservationStatus) =>
            ReservationService.updateReservation({
                ...reservation,
                status,
            }),
        {
            onSuccess: (updatedReservation: Reservation) => {
                const store = useReadStore.getState();
                patchReadStore({
                    assignedReservations: store.assignedReservations.map(item =>
                        item.reservationId === reservationItem.reservationId
                        || item.restaurantReservation.id === updatedReservation.id
                            ? {...item, restaurantReservation: updatedReservation}
                            : item
                    ),
                });
                queryClient.invalidateQueries({queryKey: ["floorplanAssigned"]});
            },
        },
    );

    return (
        <ReservationStatusSelect
            value={reservation.status}
            isExistingReservation={Boolean(reservation.id)}
            disabled={readOnly || reservationMutation.isLoading}
            fullWidth
            ariaLabel={ariaLabel}
            open={open}
            onOpen={onOpen}
            onClose={onClose}
            menuProps={{
                sx: {zIndex: 100000},
                MenuListProps: {dense: true},
                PaperProps: {
                    sx: {
                        "& .MuiMenuItem-root": {
                            minHeight: 40,
                            fontSize: "0.82rem",
                        },
                    },
                },
            }}
            onChange={status => reservationMutation.mutate(status)}
            sx={sx}
        />
    );
};

export default FloorplanReservationStatusSelect;
